import React, {createContext, useCallback, useContext, useEffect, useRef} from "react";
import {
    ExportableFile,
    OcrBatchIdResponse,
    OcrBatchResponse,
    OcrFileContentType,
    OcrFileExportFormat,
    OcrOptions,
    OcrProcessedFile
} from "../models/ocr.models";
import {ocrService} from "../services/ocr.service";
import {BehaviorSubject, Subscription} from "rxjs";
import {useSnackbar} from "./SnackbarContext";
import {getOcrFileTypeBadge} from "../utils/ocr-utils";
import {useTranslation} from "react-i18next";
import {FEATURE_KEYS} from "../models/admin-config.models";
import {useFeatureConfig} from "./FeatureConfigContext";

// --- Types ---

interface OcrBatchContextValue {
    batches$: BehaviorSubject<OcrBatchResponse[]>;
    history$: BehaviorSubject<OcrBatchIdResponse[]>;
    startBatch: (files: File[], options: OcrOptions) => Promise<number>;
    getBatch: (id: number) => OcrBatchResponse | undefined;
    updateBatches: (updater: (prev: OcrBatchResponse[]) => OcrBatchResponse[]) => void;
    refreshHistory: () => Promise<void>;
    deleteOcrBatch: (id: number) => Promise<void>;
    pinOcrBatch: (id: number) => Promise<void>;
    unpinOcrBatch: (id: number) => Promise<void>;
    patchOcrBatchTitle: (id: number, title: string) => Promise<void>;
    exportFiles: ({files, format}: {files: ExportableFile[]; format: OcrFileExportFormat}) => Promise<void>;
    retryFileProcessing: (batchId: number, fileId: number) => void;
}

// --- Context ---

const OcrBatchContext = createContext<OcrBatchContextValue | null>(null);

// --- Provider ---

export const OcrBatchProvider = ({children}: {children: React.ReactNode}) => {
    const batches$ = useRef(new BehaviorSubject<OcrBatchResponse[]>([]));
    const subscriptions$ = useRef<Map<number, Subscription>>(new Map());
    const history$ = useRef(new BehaviorSubject<OcrBatchIdResponse[]>([]));

    const { isFeatureEnabled } = useFeatureConfig();
    const isOcrEnabled = isFeatureEnabled(FEATURE_KEYS.OCR_KEY);

    const {t} = useTranslation();
    const { showSnackbar } = useSnackbar();

    // TODO: odpala się po wejściu do widoku na developie
    // turn off active subscriptions on unmount
    useEffect(() => {
        let cancelled = false;
        if (!isOcrEnabled) return;

        ocrService.getOcrHistory()
            .then(data => {
                if (!cancelled)
                    history$.current.next(data);
            })
            .catch(() => showSnackbar(t('ocr.messages.fetchOcrHistoryError'), 'error'));

        return () => {
            cancelled = true;
            subscriptions$.current.forEach(sub => sub.unsubscribe());
            subscriptions$.current.clear();
        };
    }, [isOcrEnabled]);

    const updateBatches = (updater: (prev: OcrBatchResponse[]) => OcrBatchResponse[]) => {
        batches$.current.next(updater(batches$.current.value));
    };

    const updateFileWithStatus = (batchId: number, event: OcrProcessedFile) => {
        updateBatches(prev =>
            prev.map(batch =>
                batch.id !== batchId ? batch : {
                    ...batch,
                    messages: batch.messages.map(msg => ({
                        ...msg,
                        files: msg.files.map(file =>
                            file.fileName !== event.fileName ? file : {
                                ...file,
                                status: event.status,
                                size: event.size,
                                pages: event.pages,
                                ...(event.id !== undefined && { id: event.id }),
                                ...(event.mode !== undefined && { mode: event.mode }),
                            }
                        ),
                    })),
                }
            )
        );
    };

    const updateFilesWithError = (batchId: number) => {
        updateBatches(prev =>
            prev.map(batch =>
                batch.id !== batchId ? batch : {
                    ...batch,
                    messages: batch.messages.map(msg => ({
                        ...msg,
                        files: msg.files.map(file =>
                            file.status === 'PROCESSING' || file.status === 'PENDING'
                                ? {
                                ...file, status: 'ERROR' }
                                : file
                        ),
                    })),
                }
            )
        );
    };

    const startBatch = async (files: File[], options: OcrOptions): Promise<number> => {
        // te dane zostają tylko dla history
        const { id: batchId } =  await ocrService.createOcrBatch();

        ocrService.getOcrHistory()
            .then(data => history$.current.next(data));

        const initialResponse: OcrBatchResponse = {
            id: batchId,
            selectedModelId: null,
            lastAnswerVariants: [],
            messages: [{
                id: Date.now(),
                author: 'USER',
                files: files.map(file => ({
                    id: Date.now(),
                    fileName: file.name,
                    status: 'PROCESSING',
                    mode: undefined,
                    size: file.size,
                    contentType: getOcrFileTypeBadge(file) as OcrFileContentType,
                    pages: null,
                })),
            }],
        };

        updateBatches(prev => [...prev, initialResponse]);

        const subscription = ocrService
            .processOcrBatch(batchId, files, options)
            .subscribe({
               next: (event) => {
                   updateFileWithStatus(batchId, event);
               },
                error: (err: Error) => {
                    updateFilesWithError(batchId);
                    subscriptions$.current.delete(batchId);
                },
                complete: async () => {
                   subscriptions$.current.delete(batchId);

                   try {
                       // adds file IDs and file modes to the response
                       const finalBatch = await ocrService.getOcrBatch(batchId);

                       updateBatches(prev =>
                            prev.map(b => b.id === batchId ? finalBatch : b)
                        );
                   } catch (e) {
                        console.error(t('ocr.messages.fetchBatchError'), e);
                        updateFilesWithError(batchId);
                        showSnackbar(t('ocr.messages.fetchBatchError'), 'error');
                   }
                }
            });

        // start of subscription
        subscriptions$.current.set(batchId, subscription);

        return batchId;
    };

    const getBatch = (id: number): OcrBatchResponse | undefined =>
        batches$.current.value.find(batch => batch.id === id);

    const refreshHistory = useCallback(async () => {
        const data = await ocrService.getOcrHistory();
        history$.current.next(data);
    }, []);

    const deleteOcrBatch = useCallback(async (id: number) => {
        await ocrService.deleteOcrBatch(id);
        await refreshHistory();
    }, [refreshHistory]);

    const pinOcrBatch = useCallback(async (id: number) => {
        await ocrService.pinOcrBatch(id);
        await refreshHistory();
    }, [refreshHistory]);

    const unpinOcrBatch = useCallback(async (id: number) => {
        await ocrService.unpinOcrBatch(id);
        await refreshHistory();
    }, [refreshHistory]);

    const patchOcrBatchTitle = useCallback(async (id: number, title: string) => {
        await ocrService.patchOcrBatchTitle(id, title);
        await refreshHistory();
    }, [refreshHistory]);

    const getFileId = (file: ExportableFile): number | undefined =>
        'fileId' in file ? file.fileId : file.id;

    const exportFiles = async ({files, format}: {files: ExportableFile[], format: OcrFileExportFormat}) => {
        const completedFiles = files.filter(f => f.status === 'SUCCESS' && getFileId(f));

        if (completedFiles.length === 0) {
            showSnackbar(t('ocr.messages.exportNoFilesError'), 'error');
            return;
        }

        try {
            for (const file of completedFiles) {
                const id = getFileId(file);
                if (!id) continue;

                const blob = await ocrService.exportOcrFile(id, format);

                const url = URL.createObjectURL(blob);
                const element = document.createElement('a');

                element.href = url;
                element.download = `${file.fileName}.${format.toLowerCase()}`;
                element.click();

                URL.revokeObjectURL(url);
            }
            showSnackbar(t('ocr.messages.exportSuccess'), 'success');
        } catch (error) {
            console.error(error);
            showSnackbar(t('ocr.messages.exportError'), 'error');
        }
    };

    const retryFileProcessing = (batchId: number, fileId: number): void => {
        const subscription = ocrService
            .retryOcrFileProcess(batchId, fileId)
            .subscribe({
                next: (event) => {
                    updateFileWithStatus(batchId, event);
                },
                error: (err: Error) => {
                    console.error(t('ocr.messages.retryFileOcrError'), err);
                    updateFilesWithError(batchId);
                    subscriptions$.current.delete(batchId);
                },
                complete: async () => {
                    try {
                        const finalBatch = await ocrService.getOcrBatch(batchId);
                        updateBatches(prev =>
                            prev.map(b => b.id === batchId ? finalBatch : b)
                        );
                    } catch (e) {
                        updateFilesWithError(batchId);
                        console.error(t('ocr.messages.fetchBatchError'), e);
                        showSnackbar(t('ocr.messages.fetchBatchError'), 'error');
                    }
                }
            });

        subscriptions$.current.set(batchId, subscription);
    };

    return (
        <OcrBatchContext.Provider value={{
            batches$: batches$.current,
            history$: history$.current,
            startBatch,
            getBatch,
            updateBatches,
            refreshHistory,
            deleteOcrBatch,
            pinOcrBatch,
            unpinOcrBatch,
            patchOcrBatchTitle,
            exportFiles,
            retryFileProcessing,
        }}>
            {children}
        </OcrBatchContext.Provider>
    );
};

// --- Hook ---

export const useOcrBatch = () => {
    const context = useContext(OcrBatchContext);
    if (!context) {
        throw new Error('useOcrBatch must be used within a OcrBatchProvider');
    }
    return context;
};